// The browser's side of learning.submit: argument validators for one queued
// learning record and one queued fluency response, and the pure checks that
// turn each into its stored row or a rejection. Convex validators cannot
// bound a string or an array, or narrow a number to ResponseId/ActionId, so
// those checks run here, per row: a row the client should have quarantined
// is rejected alone instead of failing the batch and blocking the queue.
import { UUID_REGEX } from "@bindery/shared/auth/authConstants";
import { SHA256_HEX_REGEX } from "@bindery/shared/crypto";
import { ActionId, ResponseId } from "@bindery/shared/contracts/fluency";
import { TreatmentIdToTreatment } from "@bindery/shared/contracts/learning";
import type { Assert, SameKeys } from "@bindery/shared/contracts/pin";
import { extractNormalizedWord } from "@bindery/shared/contracts/word";
import type { WithoutSystemFields } from "convex/server";
import { v, type Infer } from "convex/values";
import type { Doc } from "../_generated/dataModel";
import {
  assignmentArm,
  clientFluencyResponseSource,
  completion,
  controlSource,
  type learningRecordContext,
  treatmentId,
} from "./validators";

export const MAX_RECORDS_PER_SUBMIT = 200;
export const MAX_RESPONSES_PER_SUBMIT = 500;

export const MAX_SESSION_ID_LENGTH = 128;
export const MAX_WORD_LENGTH = 256;
export const MAX_READING_ID_LENGTH = 128;
export const MAX_CHOICES = 32;
export const MAX_CHOICE_LENGTH = 256;
export const MAX_HISTORY_LENGTH = 256;
export const MAX_STAMP_LENGTH = 128;

// ../app stored only the last 16 preceding responses; kept so the research
// value means the same thing on both sides of the cutover.
export const PRECEDING_FLUENCY_RECORD_KEPT = 16;

export const incomingContext = v.object({
  word: v.optional(v.string()),
  prediction: v.optional(
    v.object({
      responseId: v.number(),
      confidence: v.optional(v.number()),
    }),
  ),
  sessionStartTime: v.optional(v.number()),
  createdAt: v.number(),
  pageStartTime: v.optional(v.number()),
  readingRate: v.optional(v.number()),
  targetFluencyRecord: v.optional(v.array(v.number())),
  precedingFluencyRecord: v.optional(v.array(v.number())),
  choicesPresented: v.optional(v.array(v.string())),
  incorrectMatches: v.optional(v.array(v.string())),
  incorrectMatchesTruncated: v.optional(v.literal(true)),
  utcOffset: v.optional(v.number()),
  wordInReading: v.optional(
    v.object({
      index: v.number(),
      readingId: v.string(),
    }),
  ),
  controlSource: v.optional(controlSource),
  completion: v.optional(completion),
  completedAt: v.optional(v.number()),
  lookBack: v.optional(v.boolean()),
  repeatTap: v.optional(v.boolean()),
  playbackFailed: v.optional(v.boolean()),
  historyCold: v.optional(v.literal(true)),
  bridgeSetVersion: v.optional(v.string()),
  servedAt: v.optional(v.number()),
});
export type IncomingContextPin = Assert<
  SameKeys<Infer<typeof incomingContext>, Infer<typeof learningRecordContext>>
>;

// Q8 of docs/plan/research-sink-design.md: optional, so a batch queued by
// a build that predates them still validates.
const researchStamps = {
  // The word as the child saw it, before extractNormalizedWord folded it.
  surface: v.optional(v.string()),
  appVersion: v.optional(v.string()),
};

export const incomingLearningRecord = v.object({
  clientRecordId: v.string(),
  sessionId: v.string(),
  word: v.string(),
  response: v.number(),
  actionId: v.number(),
  treatmentId,
  context: incomingContext,
  // The public id of the orientation call the tap was made in; learning.submit
  // stores it only for a call whose reader is the caller.
  orientationSessionId: v.optional(v.string()),
  ...researchStamps,
  // The fluency model release the record's prediction was made with.
  modelRelease: v.optional(v.string()),
  vocabRelease: v.optional(v.string()),
});
export type IncomingLearningRecord = Infer<typeof incomingLearningRecord>;

export const incomingFluencyResponse = v.object({
  clientResponseId: v.string(),
  word: v.string(),
  response: v.number(),
  source: clientFluencyResponseSource,
  occurredAt: v.number(),
  sessionId: v.string(),
  readingId: v.optional(v.string()),
  wordIndex: v.optional(v.number()),
  clientRecordId: v.optional(v.string()),
  orientationSessionId: v.optional(v.string()),
  // The word's study arm the device applied (what the child saw); the server
  // commits it if this response leaves the word in developing.
  assignmentArm: v.optional(assignmentArm),
  ...researchStamps,
});
export type IncomingFluencyResponse = Infer<typeof incomingFluencyResponse>;

export const REJECTION_REASONS = [
  "not_a_uuid",
  "not_a_sha256_hex",
  "too_long",
  "not_finite",
  "not_a_non_negative_integer",
  "not_a_response_id",
  "not_an_action_id",
  "out_of_range",
  "not_session_reader",
] as const;
export type RejectionReason = (typeof REJECTION_REASONS)[number];

export interface Rejection {
  field: string;
  reason: RejectionReason;
}

export const SKIP_REASONS = ["empty_word"] as const;
export type SkipReason = (typeof SKIP_REASONS)[number];

// A word with no letters (a number, punctuation, an emoji, a non-Latin token)
// is not research data: it is dropped rather than rejected, so the client
// neither quarantines nor re-sends it, and no "" fluency row ever exists.
export const hasNoWord = (row: { word: string }): boolean => row.word === "";

export type Checked<T> =
  | { ok: true; value: T }
  | { ok: false; rejection: Rejection };

type Check = Rejection | null;

const RESPONSE_IDS: readonly number[] = Object.values(ResponseId);
const ACTION_IDS: readonly number[] = Object.values(ActionId);

const reject = (field: string, reason: RejectionReason): Rejection => ({
  field,
  reason,
});

const firstRejection = (checks: readonly Check[]): Check =>
  checks.find((check) => check !== null) ?? null;

const uuid = (field: string, value: string | undefined): Check =>
  value === undefined || UUID_REGEX.test(value)
    ? null
    : reject(field, "not_a_uuid");

const sha256Hex = (field: string, value: string | undefined): Check =>
  value === undefined || SHA256_HEX_REGEX.test(value)
    ? null
    : reject(field, "not_a_sha256_hex");

const maxLength = (
  field: string,
  value: string | undefined,
  max: number,
): Check =>
  value === undefined || value.length <= max ? null : reject(field, "too_long");

const finite = (field: string, value: number | undefined): Check =>
  value === undefined || Number.isFinite(value)
    ? null
    : reject(field, "not_finite");

const index = (field: string, value: number | undefined): Check =>
  value === undefined || (Number.isSafeInteger(value) && value >= 0)
    ? null
    : reject(field, "not_a_non_negative_integer");

const responseId = (field: string, value: number): Check =>
  RESPONSE_IDS.includes(value) ? null : reject(field, "not_a_response_id");

const actionId = (field: string, value: number): Check =>
  ACTION_IDS.includes(value) ? null : reject(field, "not_an_action_id");

const strings = (
  field: string,
  values: readonly string[] | undefined,
): Check => {
  if (values === undefined) return null;
  if (values.length > MAX_CHOICES) return reject(field, "too_long");
  return firstRejection(
    values.map((value) => maxLength(field, value, MAX_CHOICE_LENGTH)),
  );
};

const history = (
  field: string,
  values: readonly number[] | undefined,
): Check => {
  if (values === undefined) return null;
  if (values.length > MAX_HISTORY_LENGTH) return reject(field, "too_long");
  return firstRejection(values.map((value) => responseId(field, value)));
};

const confidence = (field: string, value: number | undefined): Check =>
  value === undefined || (Number.isFinite(value) && value >= 0 && value <= 1)
    ? null
    : reject(field, "out_of_range");

const checkContext = (context: Infer<typeof incomingContext>): Check =>
  firstRejection([
    maxLength("context.word", context.word, MAX_WORD_LENGTH),
    context.prediction === undefined
      ? null
      : responseId(
          "context.prediction.responseId",
          context.prediction.responseId,
        ),
    confidence("context.prediction.confidence", context.prediction?.confidence),
    finite("context.sessionStartTime", context.sessionStartTime),
    finite("context.createdAt", context.createdAt),
    finite("context.pageStartTime", context.pageStartTime),
    finite("context.readingRate", context.readingRate),
    history("context.targetFluencyRecord", context.targetFluencyRecord),
    history("context.precedingFluencyRecord", context.precedingFluencyRecord),
    strings("context.choicesPresented", context.choicesPresented),
    strings("context.incorrectMatches", context.incorrectMatches),
    finite("context.utcOffset", context.utcOffset),
    index("context.wordInReading.index", context.wordInReading?.index),
    maxLength(
      "context.wordInReading.readingId",
      context.wordInReading?.readingId,
      MAX_READING_ID_LENGTH,
    ),
    finite("context.completedAt", context.completedAt),
    finite("context.servedAt", context.servedAt),
    sha256Hex("context.bridgeSetVersion", context.bridgeSetVersion),
  ]);

const checkStamps = (row: {
  surface?: string;
  appVersion?: string;
  modelRelease?: string;
  vocabRelease?: string;
}): Check =>
  firstRejection([
    maxLength("surface", row.surface, MAX_WORD_LENGTH),
    maxLength("appVersion", row.appVersion, MAX_STAMP_LENGTH),
    maxLength("modelRelease", row.modelRelease, MAX_STAMP_LENGTH),
    maxLength("vocabRelease", row.vocabRelease, MAX_STAMP_LENGTH),
  ]);

const toInt64s = (values: readonly number[]): bigint[] => values.map(BigInt);

export type StoredContext = Infer<typeof learningRecordContext>;

// Only called once checkContext passed, so every BigInt here is of an integer.
export function toStoredContext(
  context: Infer<typeof incomingContext>,
): StoredContext {
  const {
    prediction,
    readingRate,
    targetFluencyRecord,
    precedingFluencyRecord,
    wordInReading,
    ...unchanged
  } = context;
  return {
    ...unchanged,
    ...(prediction && {
      prediction: {
        responseId: BigInt(prediction.responseId),
        ...(prediction.confidence !== undefined && {
          confidence: prediction.confidence,
        }),
      },
    }),
    // ../app rounded a fractional reading rate rather than dropping the
    // record; kept so the stored rate means the same thing.
    ...(readingRate !== undefined && {
      readingRate: BigInt(Math.round(readingRate)),
    }),
    ...(targetFluencyRecord && {
      targetFluencyRecord: toInt64s(targetFluencyRecord),
    }),
    ...(precedingFluencyRecord && {
      precedingFluencyRecord: toInt64s(
        precedingFluencyRecord.slice(-PRECEDING_FLUENCY_RECORD_KEPT),
      ),
    }),
    ...(wordInReading && {
      wordInReading: {
        index: BigInt(wordInReading.index),
        readingId: wordInReading.readingId,
      },
    }),
  };
}

export type LearningRecordRow = WithoutSystemFields<Doc<"learningRecords">>;

export function checkLearningRecord(
  record: IncomingLearningRecord,
  readerId: string,
  createdAt: number,
): Checked<LearningRecordRow> {
  const rejection = firstRejection([
    uuid("clientRecordId", record.clientRecordId),
    maxLength("sessionId", record.sessionId, MAX_SESSION_ID_LENGTH),
    maxLength("word", record.word, MAX_WORD_LENGTH),
    responseId("response", record.response),
    actionId("actionId", record.actionId),
    checkContext(record.context),
    checkStamps(record),
  ]);
  if (rejection) return { ok: false, rejection };
  const { surface, appVersion, modelRelease, vocabRelease } = record;
  return {
    ok: true,
    value: {
      readerId,
      clientRecordId: record.clientRecordId,
      sessionId: record.sessionId,
      word: extractNormalizedWord(record.word),
      response: BigInt(record.response),
      actionId: BigInt(record.actionId),
      treatmentId: record.treatmentId,
      treatment: TreatmentIdToTreatment[record.treatmentId],
      context: toStoredContext(record.context),
      ...(surface !== undefined && { surface }),
      ...(appVersion !== undefined && { appVersion }),
      ...(modelRelease !== undefined && { modelRelease }),
      ...(vocabRelease !== undefined && { vocabRelease }),
      createdAt,
    },
  };
}

export type FluencyResponseRow = WithoutSystemFields<Doc<"fluencyResponses">>;

export function checkFluencyResponse(
  response: IncomingFluencyResponse,
  readerId: string,
  createdAt: number,
): Checked<FluencyResponseRow> {
  const rejection = firstRejection([
    uuid("clientResponseId", response.clientResponseId),
    maxLength("word", response.word, MAX_WORD_LENGTH),
    responseId("response", response.response),
    finite("occurredAt", response.occurredAt),
    maxLength("sessionId", response.sessionId, MAX_SESSION_ID_LENGTH),
    maxLength("readingId", response.readingId, MAX_READING_ID_LENGTH),
    index("wordIndex", response.wordIndex),
    uuid("clientRecordId", response.clientRecordId),
    checkStamps(response),
  ]);
  if (rejection) return { ok: false, rejection };
  const { wordIndex, readingId, clientRecordId, surface, appVersion } =
    response;
  return {
    ok: true,
    value: {
      readerId,
      clientResponseId: response.clientResponseId,
      word: extractNormalizedWord(response.word),
      response: BigInt(response.response),
      source: response.source,
      occurredAt: response.occurredAt,
      sessionId: response.sessionId,
      ...(readingId !== undefined && { readingId }),
      ...(wordIndex !== undefined && { wordIndex: BigInt(wordIndex) }),
      ...(clientRecordId !== undefined && { clientRecordId }),
      ...(surface !== undefined && { surface }),
      ...(appVersion !== undefined && { appVersion }),
      createdAt,
    },
  };
}

// FR-08: a held tap's provisional copy (no completedAt) may reach the server
// before its final copy under the same clientRecordId; only the final copy
// may replace it, never the reverse.
/** A held tap's provisional copy: sent "abandoned" with no completedAt, so
 *  its final copy may still come. An ordinary tap carries neither. */
export const isProvisionalCopy = (context: StoredContext): boolean =>
  context.completion === "abandoned" && context.completedAt === undefined;

export const isFinalCopyOfProvisional = (
  stored: StoredContext,
  incoming: StoredContext,
): boolean =>
  stored.completedAt === undefined && incoming.completedAt !== undefined;
